// Empire Apps Chassis · connectors/financial/types
// Shared types for every financial connector.
// Branded IDs, uniform Result shape, status discriminator.

export type Brand<T, B extends string> = T & { readonly __brand: B };

export type BankId = Brand<string, 'BankId'>;
export type BrokerId = Brand<string, 'BrokerId'>;
export type FIPId = Brand<string, 'FIPId'>;
export type AccountId = Brand<string, 'AccountId'>;
export type FolioId = Brand<string, 'FolioId'>;
export type UAN = Brand<string, 'UAN'>;
export type PAN = Brand<string, 'PAN'>;
export type Paise = Brand<number, 'Paise'>;
export type IsoTs = Brand<string, 'IsoTs'>;

export const bankId = (s: string): BankId => s as BankId;
export const brokerId = (s: string): BrokerId => s as BrokerId;
export const fipId = (s: string): FIPId => s as FIPId;
export const accountId = (s: string): AccountId => s as AccountId;
export const folioId = (s: string): FolioId => s as FolioId;
export const uan = (s: string): UAN => s as UAN;
export const pan = (s: string): PAN => s as PAN;
export const paise = (n: number): Paise => Math.round(n) as Paise;
export const isoTs = (): IsoTs => new Date().toISOString() as IsoTs;

export type ConnectorMode = 'mock' | 'live';

export interface ConnectorStatus {
  readonly id: string;
  readonly displayName: string;
  readonly category: ConnectorCategory;
  readonly mode: ConnectorMode;
  readonly connected: boolean;
  readonly lastSync: IsoTs | null;
  readonly accountCount: number;
  readonly disabledReason: string | null;
}

export type ConnectorCategory =
  | 'account-aggregator'
  | 'email-statement'
  | 'broker'
  | 'mutual-fund'
  | 'epf'
  | 'tax'
  | 'credit'
  | 'payment-gateway'
  | 'bill-pay'
  | 'upi'
  | 'sms-inbox';

export type ConnectorResult<T> =
  | { readonly ok: true; readonly data: T; readonly note?: string }
  | { readonly ok: false; readonly reason: string; readonly retryable: boolean };

export const ok = <T,>(data: T, note?: string): ConnectorResult<T> =>
  note === undefined ? { ok: true as const, data } : { ok: true as const, data, note };
export const fail = <T,>(reason: string, retryable = true): ConnectorResult<T> =>
  ({ ok: false as const, reason, retryable });

// --------------------------------------------------------------------------
// Domain payloads
// --------------------------------------------------------------------------

export interface AccountSnapshot {
  readonly accountId: AccountId;
  readonly bankId: BankId;
  readonly maskedNumber: string;
  readonly type: 'savings' | 'current' | 'salary' | 'nro' | 'nre' | 'fd' | 'rd';
  readonly balancePaise: Paise;
  readonly currency: 'INR';
  readonly asOf: IsoTs;
}

export interface BankTransaction {
  readonly txId: string;
  readonly accountId: AccountId;
  readonly amountPaise: Paise;
  readonly direction: 'debit' | 'credit';
  readonly narration: string;
  readonly merchant: string | null;
  readonly category: string | null;
  readonly bookedAt: IsoTs;
  readonly mode: 'upi' | 'imps' | 'neft' | 'rtgs' | 'card' | 'cash' | 'auto-debit' | 'salary' | 'unknown';
}

export interface HoldingPosition {
  readonly brokerId: BrokerId;
  readonly symbol: string;
  readonly exchange: 'NSE' | 'BSE' | 'MCX' | 'NCDEX';
  readonly quantity: number;
  readonly avgCostPaise: Paise;
  readonly ltpPaise: Paise;
  readonly asOf: IsoTs;
}

export interface MutualFundFolio {
  readonly folioId: FolioId;
  readonly amc: string;
  readonly schemeCode: string;
  readonly schemeName: string;
  readonly units: number;
  readonly latestNavPaise: Paise;
  readonly currentValuePaise: Paise;
  readonly investedPaise: Paise;
  readonly asOf: IsoTs;
}

export interface EpfSnapshot {
  readonly uan: UAN;
  readonly memberId: string;
  readonly establishmentId: string;
  readonly balancePaise: Paise;
  readonly employeeSharePaise: Paise;
  readonly employerSharePaise: Paise;
  readonly pensionPaise: Paise;
  readonly asOf: IsoTs;
  readonly lastContributionAt: IsoTs;
}

export interface CreditReport {
  readonly bureau: 'CIBIL' | 'Experian' | 'Equifax' | 'CRIF';
  readonly score: number;
  readonly tier: 'poor' | 'fair' | 'good' | 'excellent';
  readonly tradelines: number;
  readonly enquiries30d: number;
  readonly utilizationPct: number;
  readonly asOf: IsoTs;
}

export interface SalarySlip {
  readonly employer: string;
  readonly month: string;
  readonly grossPaise: Paise;
  readonly netPaise: Paise;
  readonly tdsPaise: Paise;
  readonly pfPaise: Paise;
  readonly esicPaise: Paise;
  readonly professionalTaxPaise: Paise;
  readonly hraPaise: Paise;
  readonly basicPaise: Paise;
  readonly allowancesPaise: Paise;
  readonly deductionsPaise: Paise;
  readonly asOf: IsoTs;
}

export interface AisTisExtract {
  readonly pan: PAN;
  readonly ay: string;
  readonly grossSalaryPaise: Paise;
  readonly tdsTotalPaise: Paise;
  readonly interestIncomePaise: Paise;
  readonly dividendsPaise: Paise;
  readonly stockTradesGrossPaise: Paise;
  readonly stockTradesNetPaise: Paise;
  readonly mfRedemptionsPaise: Paise;
  readonly capitalGainsLtcgPaise: Paise;
  readonly capitalGainsStcgPaise: Paise;
  readonly asOf: IsoTs;
}

export interface RecurringBill {
  readonly billerId: string;
  readonly biller: string;
  readonly category: 'electricity' | 'mobile' | 'broadband' | 'dth' | 'gas' | 'water' | 'insurance' | 'rent' | 'other';
  readonly avgPaise: Paise;
  readonly nextDueDate: string | null;
  readonly autopay: boolean;
}

export interface RazorpayPaymentRecord {
  readonly paymentId: string;
  readonly orderId: string | null;
  readonly amountPaise: Paise;
  readonly status: 'authorized' | 'captured' | 'refunded' | 'failed';
  readonly method: string;
  readonly email: string | null;
  readonly contact: string | null;
  readonly createdAt: IsoTs;
}

// --------------------------------------------------------------------------
// Connector contract — uniform across all 12 financial connectors.
// --------------------------------------------------------------------------

export interface ConnectorPayload {
  readonly accounts?: readonly AccountSnapshot[];
  readonly transactions?: readonly BankTransaction[];
  readonly holdings?: readonly HoldingPosition[];
  readonly mfFolios?: readonly MutualFundFolio[];
  readonly epf?: EpfSnapshot;
  readonly credit?: CreditReport;
  readonly slips?: readonly SalarySlip[];
  readonly ais?: AisTisExtract;
  readonly bills?: readonly RecurringBill[];
  readonly razorpay?: readonly RazorpayPaymentRecord[];
  readonly raw?: Record<string, unknown>;
}

export interface ConnectorContext {
  readonly principalId: string;
  readonly dpdpLog: (action: string, meta: Record<string, string | number>) => void;
  readonly nowIso: () => IsoTs;
}

export interface Connector {
  readonly id: string;
  readonly displayName: string;
  readonly category: ConnectorCategory;
  readonly defaultMode: ConnectorMode;
  status(ctx: ConnectorContext): Promise<ConnectorStatus>;
  connect(ctx: ConnectorContext, opts?: Record<string, string>): Promise<ConnectorResult<{ readonly connectedAt: IsoTs }>>;
  import(ctx: ConnectorContext, opts?: { readonly fileBlob?: Blob; readonly since?: IsoTs }): Promise<ConnectorResult<ConnectorPayload>>;
  disconnect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly disconnectedAt: IsoTs }>>;
  mockData(): ConnectorPayload;
}
